import mongoose, { Model, Schema } from 'mongoose';
import { AvailableStatusEnum } from '../../../Modules/availabilityManagement/interfaces/availability.interface';
import { AllocationRequestStatusEnum, IAllocationRequest } from '../interfaces/allocationRequest.interface';
import { RolesEnum } from '../../../Modules/Auth/interfaces/auth.interface';
import { HttpException } from '../../../utils/exceptions/httpException';
import { status } from '../../../utils/helpers/api.responses';

const AllocationRequestSchema = new Schema<IAllocationRequest>(
    {
        userId: {
            type: Schema.Types.ObjectId,
            required: true,
            ref: 'User',
        },
        requestDate: {
            type: Date,
            required: true,
            default: Date.now,
        },
        message: {
            type: String,
            trim: true,
        },
        startDate: {
            type: Date,
            required: true,
        },
        endDate: {
            type: Date,
            required: true,
        },
        // location: {
        //     type: String,
        //     required: true,
        //     trim: true,
        // },
        wage: {
            type: Number,
            required: false,
            min: 0,
        },
        status: {
            type: String,
            enum: Object.values(AllocationRequestStatusEnum),
            default: AllocationRequestStatusEnum.PENDING,
        },
        responseNotes: {
            type: String,
            trim: true,
        },
        respondedAt: {
            type: Date,
        },
        // companyName: {
        //     type: String,
        //     trim: true,
        // },
        jobId: {
            type: Schema.Types.ObjectId,
            ref: 'Job',
            required: true,
        },
        createdBy: {
            type: Schema.Types.ObjectId,
            ref: 'User',
            required: true,
        },
        updatedBy: {
            type: Schema.Types.ObjectId,
            ref: 'User',
        },
        isDeleted: {
            type: Boolean,
            default: false,
        },
        deletedAt: {
            type: Date,
        },
        allocationNotes: {
            type: String,
            trim: true,
            default: '',
        },
    },
    {
        timestamps: { createdAt: 'created_at', updatedAt: 'updated_at' },
        versionKey: false,
    }
);

AllocationRequestSchema.pre('save', async function (next) {
    try {
        if (!this.isNew) {
            return next();
        }
        const session = this.$session();
        const queryOpts = session ? { session } : {};

        const userId = this.userId;
        const jobId = this.jobId;

        const user = await mongoose.model('User')
            .findById(userId)
            .select('roleId')
            .setOptions(session)
            .lean();
        if (!user) {
            throw new HttpException(status.BadRequest, 'User not found');
        }

        const role = await mongoose.model('Role')
            .findById((user as any).roleId)
            .setOptions(session)
            .lean();
        if (!role || (role as any).name.toLowerCase() !== RolesEnum.LABOUR.toLowerCase()) {
            throw new HttpException(status.BadRequest, 'Invalid labour user');
        }

        const job = await mongoose.model('Job')
            .findById(jobId)
            .setOptions(session)
            .lean();
        if (!job || (job as any).isDeleted || (job as any).status === 'Closed') {
            throw new HttpException(status.BadRequest, 'Job not found or closed');
        }
        if ((job as any).open <= 0) {
            throw new HttpException(status.BadRequest, 'No open spots available for this job');
        }

        const unavailableLabour = await mongoose.model('Availability')
            .findOne({
                userId,
                startDate: { $lte: this.endDate },
                endDate: { $gte: this.startDate },
                isDeleted: false,
                status: AvailableStatusEnum.UNAVAILABLE,
            })
            .select('userId')
            .setOptions(session)
            .lean();
        if (unavailableLabour) {
            const unavailableUser = await mongoose.model('User')
                .findById((unavailableLabour as any).userId)
                .select('firstName lastName email')
                .setOptions(session)
                .lean();
            const name = (unavailableUser as any)?.firstName || (unavailableUser as any)?.email || 'Unknown';
            throw new HttpException(
                status.BadRequest,
                `Labour is unavailable for the requested period: ${name}`
            );
        }

        const existingRequest = await mongoose.model('AllocationRequest')
            .findOne({
                userId,
                startDate: { $lte: this.endDate },
                endDate: { $gte: this.startDate },
                isDeleted: false,
                status: AllocationRequestStatusEnum.PENDING,
            })
            .setOptions(session)
            .lean();
        if (existingRequest) {
            throw new HttpException(
                status.BadRequest,
                'An allocation request already exists for this user and date range'
            );
        }

        next();
    } catch (error) {
        console.error('Error in AllocationRequest pre-save hook:', error);
        next(error);
    }
});

AllocationRequestSchema.index({ userId: 1, startDate: 1, endDate: 1, isDeleted: 1 });

const AllocationRequest: Model<IAllocationRequest> = mongoose.model<IAllocationRequest>('AllocationRequest', AllocationRequestSchema);
export default AllocationRequest;

// AllocationRequestSchema.pre('save', async function (next) {
//     const allocationRequest = this as IAllocationRequest;

//     if (allocationRequest.endDate < allocationRequest.startDate) {
//         return next(new Error('End date cannot be before start date'));
//     }

//     const users = await mongoose.model<IUser>('User')
//         .find({ _id: { $in: allocationRequest.userId } })
//         .select('roleId')
//         .lean();
//     const roleIds = users.map(user => user.roleId);
//     const roles = await mongoose.model('Role')
//         .find({ _id: { $in: roleIds } })
//         .lean();
//     const invalidUsers = users.filter(user => {
//         const role = roles.find(r => r._id.toString() === user.roleId.toString());
//         return !role || role.name.toLowerCase() !== RolesEnum.LABOUR.toLowerCase();
//     });
//     if (invalidUsers.length > 0) {
//         return next(new HttpException(status.BadRequest, 'All userIds must belong to Labour users'));
//     }

//     const conflictingAvailability = await mongoose.model<IAvailability>('Availability')
//         .find({
//             userId: { $in: allocationRequest.userId },
//             isDeleted: false,
//             status: AvailableStatusEnum.UNAVAILABLE,
//             $or: [
//                 { startDate: { $lte: allocationRequest.endDate }, endDate: { $gte: allocationRequest.startDate } },
//             ],
//         })
//         .lean();

//     if (conflictingAvailability) {
//         return next(new HttpException(status.BadRequest, 'Labour is unavailable for the requested period'));
//     }

//     next();
// });